import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { postScheduledQueryKey, togglePin } from '../../api/posts'
import { PostRow } from '../../generated/api/post-row'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { useToastNotifications } from '../../hooks/useToastNotification'
import { ToastType } from '../common/Toast/toast-type'
import {
  cancelPostRows,
  invalidatePostRows,
  pinnedRow,
  removePinnedRow,
  restorePinnedRows,
  resumePostRows,
  snapshotPinnedRows,
  updatePostRow,
} from './postRowCache'

export const usePinFailureToast = (): ((error: unknown) => void) => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  return (error) => {
    const errData = isAxiosError(error)
      ? (error.response?.data as { code?: number; err?: string } | undefined)
      : undefined
    const limitMatch =
      errData?.code === 400 ? errData.err?.match(/(\d+)\s+pinned posts/i) : null

    if (limitMatch) {
      setToastNotification(
        ToastType.DANGER,
        translate('post.flashMessage.pinLimitReached', {
          max: Number(limitMatch[1]),
        })
      )
    } else {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    }
  }
}

export const useTogglePin = (
  row: Pick<PostRow, 'id' | 'isScheduled'>
): ((value: boolean) => void) => {
  const showPinFailure = usePinFailureToast()
  const queryClient = useQueryClient()
  const { id: postId, isScheduled } = row

  const { mutate } = useMutation({
    mutationFn: async (value: boolean) => {
      return togglePin(postId, value)
    },
    meta: { skipGlobalInvalidate: true },
    onMutate: async (value: boolean) => {
      const interrupted = await cancelPostRows(queryClient)
      const previousPinned = snapshotPinnedRows(queryClient)
      updatePostRow(queryClient, postId, (_row) => pinnedRow(_row, value))
      if (!value) {
        removePinnedRow(queryClient, postId)
      }
      return { previousPinned, interrupted }
    },
    onError: (error, value, context) => {
      updatePostRow(queryClient, postId, (_row) => pinnedRow(_row, !value))
      if (context) {
        restorePinnedRows(queryClient, context.previousPinned)
      }
      showPinFailure(error)
    },
    onSettled: (_data, _error, _value, context) => {
      if (isScheduled) {
        queryClient.invalidateQueries({ queryKey: postScheduledQueryKey })
      } else {
        invalidatePostRows(queryClient)
      }
      resumePostRows(queryClient, context?.interrupted)
    },
  })

  return mutate
}
